import { motion } from 'framer-motion';
import { Instagram, MapPin, ExternalLink, MessageCircle, Mail, Phone, Calendar } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useScrollReveal, staggerContainer, staggerItem } from '../hooks/useScrollReveal';

const Contact = () => {
  const { ref, isInView } = useScrollReveal();
  const { t, language } = useLanguage();

  const socialLinks = [
    {
      icon: Instagram,
      label: 'Instagram',
      url: 'https://www.instagram.com/martin.senk_/',
      color: 'hover:text-pink-400'
    },
    {
      icon: MessageCircle,
      label: 'WhatsApp',
      url: 'https://api.whatsapp.com/send?phone=420775648863',
      color: 'hover:text-green-400'
    }
  ];

  return (
    <section id="contact" className="section-padding bg-gradient-to-b from-gray-900 to-black">
      <div className="container-max">
        <motion.div
          ref={ref}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={staggerContainer}
          className="reveal-section"
        >
          <motion.h2
            variants={staggerItem}
            className="text-4xl md:text-5xl font-bold text-center mb-16"
          >
            {t.contact.title.includes(' & ') ? (
              <>
                {t.contact.title.split(' & ')[0]} & <span className="text-primary-400">{t.contact.title.split(' & ')[1]}</span>
              </>
            ) : (
              <span className="text-primary-400">{t.contact.title}</span>
            )}
          </motion.h2>

          <div className="grid md:grid-cols-2 gap-12 items-start">
            {/* Contact Info */}
            <motion.div variants={staggerItem} className="space-y-8">
              <div>
                <h3 className="text-2xl font-semibold mb-4">{t.contact.heading}</h3>
                <p className="text-lg text-gray-300 leading-relaxed mb-6">
                  {t.contact.paragraph1}
                </p>
                <p className="text-lg text-gray-300 leading-relaxed">
                  {t.contact.paragraph2}
                </p>
              </div>

              {/* Social Links */}
              <div className="space-y-4">
                <h4 className="text-xl font-semibold mb-4">{t.contact.followMe}</h4>
                {socialLinks.map((social, index) => (
                  <a
                    key={index}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex items-center space-x-3 text-gray-300 ${social.color} transition-all duration-300 group hover:translate-x-2`}
                  >
                    <social.icon className="w-6 h-6 group-hover:scale-110 transition-transform" />
                    <span className="text-lg">{social.label}</span>
                    <ExternalLink className="w-4 h-4 opacity-50 group-hover:opacity-100" />
                  </a>
                ))}
              </div>
            </motion.div>

            {/* Gym Info & Booking */}
            <motion.div variants={staggerItem} className="space-y-8">
              {/* Gym Location */}
              <div className="bg-gradient-to-br from-gray-800 to-gray-900 p-6 rounded-lg border border-primary-500/20">
                <div className="flex items-start space-x-4 mb-4">
                  <MapPin className="w-8 h-8 text-primary-400 mt-1" />
                  <div>
                    <h4 className="text-xl font-semibold mb-2">{t.contact.gymTitle}</h4>
                    <p className="text-gray-300 mb-4">
                      {t.contact.gymDescription}
                    </p>
                    <a
                      href="https://www.crossfitdestiny.cz/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center space-x-2 text-primary-400 hover:text-primary-300 transition-colors"
                    >
                      <span>{t.contact.visitGym}</span>
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                </div>
                {/* Google Maps - Screenshot with link */}
                <div className="mt-4">
                  <a
                    href="https://maps.app.goo.gl/SfQfr8QVaYc8iJG49"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block group"
                  >
                    <div className="relative w-full h-48 rounded-lg overflow-hidden border border-primary-500/20">
                      <img
                        src="/map.webp"
                        alt={t.contact.gymTitle}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                        <div className="text-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <MapPin className="w-8 h-8 text-primary-400 mx-auto mb-1" />
                          <p className="text-primary-400 text-xs font-semibold">
                            {t.contact.openInGoogleMaps}
                          </p>
                        </div>
                      </div>
                    </div>
                  </a>
                </div>
              </div>

              {/* Contact CTA */}
              <div className="bg-gradient-to-br from-primary-900/20 to-primary-800/20 p-8 rounded-lg border border-primary-500/30 text-center backdrop-blur-custom hover:scale-[1.01] transition-transform duration-300">
                <h4 className="text-2xl font-semibold mb-4">{t.contact.bookingTitle}</h4>
                <p className="text-gray-300 mb-6">
                  {t.contact.bookingDescription}
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center flex-wrap">
                  <a
                    href="https://book.martinsenk.cz"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105 hover:shadow-lg w-full sm:w-[220px]"
                  >
                    <Calendar className="w-5 h-5" />
                    {t.hero.ctaOnline}
                  </a>
                  <a
                    href={`mailto:${t.contact.email}?subject=${encodeURIComponent(language === 'cs' ? 'Rezervace tréninku' : 'Training Booking')}&body=${encodeURIComponent(t.contact.emailMessage)}`}
                    className="inline-flex items-center justify-center gap-2 bg-gray-600 hover:bg-gray-700 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105 hover:shadow-lg w-full sm:w-[220px]"
                  >
                    <Mail className="w-5 h-5" />
                    {t.contact.emailButton}
                  </a>
                  <a
                    href={`https://api.whatsapp.com/send?phone=${t.contact.phoneNumber.replace(/\+/g, '')}&text=${encodeURIComponent(t.contact.whatsappMessage)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105 hover:shadow-lg w-full sm:w-[220px]"
                  >
                    <MessageCircle className="w-5 h-5" />
                    {t.contact.whatsappButton}
                  </a>
                  <a
                    href={`tel:${t.contact.phoneNumber}`}
                    className="inline-flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105 hover:shadow-lg w-full sm:w-[220px]"
                  >
                    <Phone className="w-5 h-5" />
                    {t.contact.phoneButton}
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;
